import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import Form from "../Form/Form.jsx";
import FormTextField from "../Form/FormTextField.jsx";
import FormPasswordField from "../Form/FormPasswordField.jsx";
import SubmitButton from "../Form/SubmitButton.jsx";
import {
  requiredPasswordSchema,
  passwordConfirmationSchema,
  passwordHelperText,
} from "../../utils/passwordValidation.js";

// Validation schema for password reset form
const validationSchema = yup.object({
  token: yup.string().required("Token ist erforderlich."),
  password: requiredPasswordSchema,
  password_confirmation: passwordConfirmationSchema("password"),
});

/**
 * Password reset form component for setting a new password with a token
 *
 * @param {Object} props - Component props
 * @param {string} props.token - Reset token from email link
 * @param {function} props.onTokenChange - Callback when token changes
 * @param {function} props.onSubmit - Callback when form is submitted with valid data
 * @param {boolean} props.isLoading - Whether the form is in loading state
 * @returns {JSX.Element}
 */
const PasswordResetForm = ({
  token = "",
  onTokenChange,
  onSubmit,
  isLoading = false,
}) => {
  const handleFormSubmit = async (data, formMethods) => {
    console.log("Form submit started with data:", data);
    console.log("Form methods available:", Object.keys(formMethods));

    if (onSubmit) {
      const success = await onSubmit(
        {
          token: data.token.trim(),
          password: data.password,
          passwordConfirmation: data.password_confirmation,
        },
        formMethods
      ); // Pass formMethods to parent for server error handling

      console.log("Password reset success?", success);
      console.log("Form errors after onSubmit:", formMethods.formState.errors);

      // If submission was successful, optionally reset the form
      if (success) {
        formMethods.reset();
      }

      return success; // Return the actual success value, not always true
    }
    return false; // Return false if no onSubmit callback
  };

  // Handle token changes to sync with parent component
  const handleTokenChange = (value) => {
    if (onTokenChange) {
      onTokenChange(value);
    }
  };

  return (
    <Form
      onSubmit={handleFormSubmit}
      resolver={yupResolver(validationSchema)}
      defaultValues={{
        token: token,
        password: "",
        password_confirmation: "",
      }}
      key={token} // Re-render form when token changes from parent
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <FormTextField
          name="token"
          label="Token"
          autoComplete="one-time-code"
          disabled={isLoading}
          onChange={(e) => handleTokenChange(e.target.value)}
        />
        <FormPasswordField
          name="password"
          label="Passwort"
          type="password"
          autoComplete="new-password"
          disabled={isLoading}
          helperText={passwordHelperText.requirements}
        />
        <FormPasswordField
          name="password_confirmation"
          label="Passwort (Wiederholung)"
          type="password"
          autoComplete="new-password"
          disabled={isLoading}
          helperText="Bestätigen Sie Ihr neues Passwort"
        />
        <SubmitButton disabled={isLoading}>
          {isLoading ? "Wird gesetzt..." : "Passwort setzen"}
        </SubmitButton>
      </Box>
    </Form>
  );
};

PasswordResetForm.propTypes = {
  token: PropTypes.string,
  onTokenChange: PropTypes.func,
  onSubmit: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default PasswordResetForm;
